import { type KeyboardEvent, useLayoutEffect, useRef } from "react";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import type { DataGridPickerEditor } from "../../columns/types";
import type { EditorMove } from "../editActions";
import { getEditorKeyMove, NO_ARROW_MOVES } from "./editorKeys";
import type { EditorProps } from "./editorProps";
import styles from "./editors.module.scss";

interface PickerEditorProps extends Pick<EditorProps, "onCancel"> {
    editor: DataGridPickerEditor;
    value: string | null;
    /** The key that opened the editor; it becomes the search's first letter. */
    seed: string | null;
    /** Commits an option's value as is, never looked up by label. */
    onCommit: (input: { value: string | null; move: EditorMove }) => void;
}

/**
 * The popover editor: the design-system Picker on a cell input trigger,
 * opened on mount. A pick with Enter moves down, with Tab right, with a click
 * stays; Tab without a pick just moves. Closing without a pick reverts.
 */
export const PickerEditor = ({
    editor,
    value,
    seed,
    onCommit,
    onCancel,
}: PickerEditorProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const moveRef = useRef<EditorMove>("stay");
    const hasPickedRef = useRef(false);
    const items: Array<PickerItemData<string>> = editor.options.map(
        (option) => ({ value: option.value, label: option.label }),
    );

    useLayoutEffect(() => {
        const input = inputRef.current;
        input?.focus();
        if (seed === null) {
            input?.select();
        } else {
            input?.setSelectionRange(input.value.length, input.value.length);
        }
    }, [seed]);

    return (
        <div
            className={styles.picker}
            data-dt-editor=""
            onKeyDownCapture={(event) => {
                moveRef.current =
                    getEditorKeyMove(event, NO_ARROW_MOVES) ?? "stay";
            }}
            onPointerDownCapture={() => {
                moveRef.current = "stay";
            }}
            onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
                if (event.key !== "Tab") {
                    return;
                }
                event.preventDefault();
                if (!hasPickedRef.current) {
                    onCommit({ value, move: moveRef.current });
                }
            }}
        >
            <Picker.Select
                items={items}
                trigger="cellInput"
                cellEditor={{ openingKey: seed }}
                ref={inputRef}
                chevron="none"
                className={styles.pickerInput}
                placeholder={editor.placeholder ?? "Search…"}
                value={value}
                defaultInputValue={seed ?? undefined}
                defaultOpen
                filterable
                emptyLabel="No matching options"
                onValueChange={(next) => {
                    if (next !== null) {
                        hasPickedRef.current = true;
                        onCommit({ value: next, move: moveRef.current });
                    }
                }}
                onOpenChange={(isOpen) => {
                    if (!isOpen && !hasPickedRef.current) {
                        onCancel();
                    }
                }}
            />
        </div>
    );
};
